import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coins, 
  Droplets, 
  Network, 
  ArrowRightLeft,
  Sparkles,
  Globe,
  ChevronDown,
  ChevronUp,
  Search,
  Eye,
  Zap,
  BookOpen
} from 'lucide-react';
import { exampleCurrencies, getCurrencyTypeInfo, currencyIdToName } from '../data/currencyExamples';
import allCurrencies from '../allcurrencies.json';
import importedCurrencies from '../importedcurrencies.json';

const currencyTypes = [
  {
    id: 'token',
    title: 'Simple Token',
    subtitle: 'Fixed or Mintable Supply',
    description: 'Create a simple token with either fixed supply (decentralized) or mintable/burnable (centralized)',
    icon: Coins,
    options: 32,
    color: 'from-blue-500 to-indigo-600',
  },
  {
    id: 'basket',
    title: 'Liquidity Pool',
    subtitle: 'Basket Currency (AMM)',
    description: 'Create a fractional reserve currency backed by up to 8 reserve currencies',
    icon: Droplets,
    options: 33,
    color: 'from-emerald-500 to-teal-600',
  },
  {
    id: 'pbaas',
    title: 'PBaaS Chain',
    subtitle: 'Independent Blockchain',
    description: 'Launch a complete independent blockchain with mining, staking, and cross-chain capabilities',
    icon: Network,
    options: 256,
    color: 'from-purple-500 to-pink-600',
  },
  {
    id: 'erc20',
    title: 'Mapped ERC-20',
    subtitle: 'Ethereum Bridge Token',
    description: 'Map an existing ERC-20 token from Ethereum to Verus via the bridge',
    icon: ArrowRightLeft,
    options: 32,
    color: 'from-orange-500 to-red-600',
  },
];

const getIconForType = (typeId) => {
  switch (typeId) {
    case 'token': return Coins;
    case 'basket': return Droplets;
    case 'pbaas': return Network;
    case 'erc20': return ArrowRightLeft;
    case 'gateway': return Globe;
    default: return Coins;
  }
};

const LandingPage = ({ onSelectType, onLoadExample, onViewExisting }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [categorySearches, setCategorySearches] = useState({
    token: '',
    basket: '',
    pbaas: '',
    erc20: '',
  });

  // Process and combine all currencies from both files
  const allProcessedCurrencies = useMemo(() => {
    const combined = [...allCurrencies, ...importedCurrencies];
    const seen = new Set();
    
    return combined.map(c => {
      const def = c.currencydefinition;
      if (!def || !def.name) return null;
      
      if (seen.has(def.currencyid)) return null;
      seen.add(def.currencyid);
      
      const typeInfo = getCurrencyTypeInfo(def.options, !!def.nativecurrencyid);
      
      let reserves = [];
      if (def.currencies) {
        reserves = def.currencies.map(id => currencyIdToName[id] || id.slice(0, 8) + '...');
      }
      
      return {
        name: def.name || def.fullyqualifiedname,
        currencyid: def.currencyid,
        options: def.options,
        typeInfo,
        reserves,
        hasNativeCurrencyId: !!def.nativecurrencyid,
        fullData: c,
      };
    }).filter(Boolean);
  }, []);

  // Categorize currencies and sort alphabetically
  const categorizedCurrencies = useMemo(() => {
    const sortByName = (a, b) => a.name.localeCompare(b.name);
    return {
      token: allProcessedCurrencies.filter(c => c.typeInfo.id === 'token').sort(sortByName),
      basket: allProcessedCurrencies.filter(c => c.typeInfo.id === 'basket').sort(sortByName),
      pbaas: allProcessedCurrencies.filter(c => c.typeInfo.id === 'pbaas').sort(sortByName),
      erc20: allProcessedCurrencies.filter(c => c.typeInfo.id === 'erc20').sort(sortByName),
    };
  }, [allProcessedCurrencies]);

  const handleCategorySearch = (categoryId, value) => {
    setCategorySearches(prev => ({ ...prev, [categoryId]: value }));
  };

  const getFilteredCurrencies = (categoryId) => {
    const currencies = categorizedCurrencies[categoryId] || [];
    const query = categorySearches[categoryId]?.toLowerCase() || '';
    if (!query) return currencies;
    return currencies.filter(c => c.name.toLowerCase().includes(query)).sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex overflow-hidden">
      
      {/* Left Panel - Examples */}
      <div className="w-64 bg-slate-800/50 border-r border-slate-700/50 flex flex-col">
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Quick Start</h3>
              <p className="text-xs text-slate-400">Pre-made templates</p>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
          {exampleCurrencies.map((example) => {
            const typeInfo = currencyTypes.find(t => t.id === example.type) || currencyTypes[0];
            const Icon = getIconForType(example.type);
            
            return (
              <motion.button
                key={example.id}
                whileHover={{ scale: 1.02, x: 2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onLoadExample(example)}
                className="w-full flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 hover:border-amber-500/30 hover:bg-slate-800/50 transition-all text-left"
              >
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${typeInfo.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-medium text-white truncate">{example.name}</h4>
                  <p className="text-xs text-slate-500 truncate">{example.description}</p>
                </div>
                <Zap className="w-3 h-3 text-amber-500 flex-shrink-0" />
              </motion.button>
            );
          })}
        </div>

        <div className="p-3 border-t border-slate-700/50">
          <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
            <BookOpen className="w-3 h-3" />
            <span>Verus Protocol</span>
          </div>
        </div>
      </div>

      {/* Main Content - Currency Type Selection */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="py-4 px-6 border-b border-slate-700/50 bg-slate-900/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Coins className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Verus Currency Builder</h1>
                <p className="text-xs text-slate-400">Visual definecurrency command generator</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                What would you like to create?
              </h2>
              <p className="text-sm text-slate-400">
                Choose a currency type to start building, or select an example from the left panel
              </p>
            </div>

            {/* Currency Type Cards - 2x2 Grid */}
            <div className="grid grid-cols-2 gap-5">
              {currencyTypes.map((type, index) => (
                <motion.button
                  key={type.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onSelectType(type)}
                  className="relative group p-6 rounded-2xl border border-slate-700/50 bg-slate-800/50 hover:border-slate-600/50 hover:bg-slate-800/80 transition-all text-left"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${type.color} opacity-0 group-hover:opacity-5 transition-opacity rounded-2xl`} />
                  
                  {/* Options badge - positioned separately */}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center shadow-lg`}>
                      <type.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="px-2.5 py-1 rounded-full bg-slate-700/50 text-xs text-slate-400">
                      opt: {type.options}
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-white mb-1.5">{type.title}</h3>
                  <p className={`text-sm font-medium bg-gradient-to-r ${type.color} bg-clip-text text-transparent mb-3`}>
                    {type.subtitle}
                  </p>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {type.description}
                  </p>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Existing Currencies */}
      <div className="w-80 bg-slate-800 border-l border-slate-700/50 flex flex-col">
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Existing Currencies</h3>
              <p className="text-xs text-slate-400">{allProcessedCurrencies.length} on Verus</p>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {currencyTypes.map((category) => {
            const isExpanded = expandedCategory === category.id;
            const currencies = getFilteredCurrencies(category.id);
            const totalCount = categorizedCurrencies[category.id]?.length || 0;
            const Icon = getIconForType(category.id);
            
            return (
              <div key={category.id} className="border-b border-slate-700/30">
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  className={`w-full flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-700 transition-colors ${isExpanded ? 'bg-slate-700' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-medium text-white">{category.title}</span>
                    <span className="text-xs text-slate-500">({totalCount})</span>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-3 pb-3">
                        {/* Search */}
                        <div className="relative mb-2">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                          <input
                            type="text"
                            value={categorySearches[category.id]}
                            onChange={(e) => handleCategorySearch(category.id, e.target.value)}
                            placeholder={`Search...`}
                            className="w-full pl-7 pr-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-xs placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                          />
                        </div>

                        {/* Currency List */}
                        <div className="max-h-48 overflow-y-auto custom-scrollbar space-y-1">
                          {currencies.map((currency) => (
                            <motion.button
                              key={currency.currencyid}
                              whileHover={{ scale: 1.02, x: 2 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => onViewExisting(currency.fullData)}
                              className="w-full flex items-center gap-2 p-2 bg-slate-900 rounded-lg border border-slate-700/50 hover:border-blue-500/50 hover:bg-slate-800 transition-all text-left"
                            >
                              <div className="flex-1 min-w-0">
                                <h5 className="text-xs font-medium text-white truncate">{currency.name}</h5>
                                {currency.reserves.length > 0 && (
                                  <p className="text-xs text-slate-500 truncate">
                                    {currency.reserves.slice(0, 2).join(', ')}
                                  </p>
                                )}
                                {currency.hasNativeCurrencyId && (
                                  <p className="text-xs text-amber-500">ERC-20</p>
                                )}
                              </div>
                              <Eye className="w-3 h-3 text-blue-400 flex-shrink-0" />
                            </motion.button>
                          ))}
                        </div>

                        {currencies.length === 0 && (
                          <p className="text-center py-2 text-xs text-slate-500">No currencies found</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Stats Footer */}
        <div className="p-3 border-t border-slate-700/50">
          <div className="grid grid-cols-4 gap-1">
            {currencyTypes.map((type) => {
              const count = categorizedCurrencies[type.id]?.length || 0;
              const Icon = getIconForType(type.id);
              return (
                <div key={type.id} className="text-center p-1">
                  <div className={`w-5 h-5 mx-auto rounded bg-gradient-to-br ${type.color} flex items-center justify-center mb-1`}>
                    <Icon className="w-3 h-3 text-white" />
                  </div>
                  <p className="text-xs font-bold text-white">{count}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
